import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query'
import { useDebounce } from '../hooks/useDebounce'
import {
  TeamGetParams,
  TeamGetResponse,
} from '../services/teamsService/getTeams'
import { useTeams } from '../hooks/useTeams'

interface FiltersType {
  teamIds?: string
  startDate?: string
  endDate?: string
  sort?: string
  order?: string
}

interface TeamsContextValue {
  teams: {
    isLoading: boolean
    isFetching: boolean
    data: InfiniteData<TeamGetResponse, unknown> | undefined
    fetchNextPage: (
      options?: FetchNextPageOptions | undefined,
    ) => Promise<
      InfiniteQueryObserverResult<InfiniteData<TeamGetResponse, unknown>, Error>
    >
    hasNextPage: boolean
    isFetchingNextPage: boolean
  }
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>
  searchValue?: string
  setFilterParams: (value: FiltersType) => void
  params: TeamGetParams | undefined
}
export const TeamsContext = createContext({} as TeamsContextValue)

export function TeamsProvider({ children }: { children: ReactNode }) {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  const debouncedSearchValue = useDebounce(500, searchValue)

  const { params, setParams, ...teams } = useTeams()

  const setFilterParams = (value: FiltersType) => {
    setParams((state) => ({ ...state, ...value }))
  }

  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }))
  }, [debouncedSearchValue, setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  return (
    <TeamsContext.Provider
      value={{
        teams,
        setSearchValue,
        searchValue,
        setFilterParams,
        params,
      }}
    >
      {children}
    </TeamsContext.Provider>
  )
}
